/**
 * Pose Filter
 * Implements Kalman filtering for smooth pose tracking
 */

import * as THREE from 'three';

/**
 * T035: Optimized Kalman Filter with TypedArray for better performance
 */
class KalmanFilter {
  constructor(processNoise = 0.01, measurementNoise = 0.1) {
    this.q = processNoise; // Process noise covariance
    this.r = measurementNoise; // Measurement noise covariance
    // T035: Use TypedArray for state variables (single allocation)
    this.state = new Float32Array(3); // [p, x, k]
    this.state[0] = 1.0; // p (estimation error covariance)
    this.state[1] = 0.0; // x (state estimate)
    this.state[2] = 0.0; // k (Kalman gain)
  }

  /**
   * Update filter with new measurement
   * @param {number} measurement - New measurement value
   * @returns {number} - Filtered value
   */
  update(measurement) {
    // T035: Direct array access (faster than object property access)
    // Prediction
    this.state[0] += this.q;

    // Update
    this.state[2] = this.state[0] / (this.state[0] + this.r);
    this.state[1] += this.state[2] * (measurement - this.state[1]);
    this.state[0] *= 1 - this.state[2];

    return this.state[1];
  }

  /**
   * Reset filter state
   */
  reset() {
    this.state[0] = 1.0;
    this.state[1] = 0.0;
    this.state[2] = 0.0;
  }

  // T035: Accessors for backward compatibility
  get p() {
    return this.state[0];
  }
  set p(value) {
    this.state[0] = value;
  }
  get x() {
    return this.state[1];
  }
  set x(value) {
    this.state[1] = value;
  }
  get k() {
    return this.state[2];
  }
}

/**
 * Pose Filter class for smooth tracking
 */
export class PoseFilter {
  constructor(options = {}) {
    // T016: Kalman filter parameters optimized for lower latency
    this.processNoise = options.processNoise || 0.005; // Reduced from 0.01
    this.measurementNoise = options.measurementNoise || 0.05; // Reduced from 0.1
    this.maxFrameBufferSize = options.maxFrameBufferSize || 3; // T016: Frame buffer size limit
    this.platform = options.platform || 'unknown'; // Store platform

    // Create separate filters for position (x, y, z)
    this.positionFilters = {
      x: new KalmanFilter(this.processNoise, this.measurementNoise),
      y: new KalmanFilter(this.processNoise, this.measurementNoise),
      z: new KalmanFilter(this.processNoise, this.measurementNoise),
    };

    // Create separate filters for rotation (Euler angles)
    this.rotationFilters = {
      x: new KalmanFilter(this.processNoise, this.measurementNoise),
      y: new KalmanFilter(this.processNoise, this.measurementNoise),
      z: new KalmanFilter(this.processNoise, this.measurementNoise),
    };

    // T016/T017: Frame buffer for latency control
    this.frameBuffer = []; // Store recent measurements (FIFO)

    // State tracking
    this.isInitialized = false;
    this.lastMatrix = null;
    this.smoothedMatrix = new THREE.Matrix4();

    // T035: Pre-allocated objects to avoid creation in filter() loop
    this._position = new THREE.Vector3();
    this._quaternion = new THREE.Quaternion();
    this._scale = new THREE.Vector3();
    this._euler = new THREE.Euler();
    this._filteredPosition = new THREE.Vector3();
    this._filteredEuler = new THREE.Euler();
    this._filteredQuaternion = new THREE.Quaternion();

    console.log(
      `PoseFilter initialized: q=${this.processNoise}, r=${this.measurementNoise}, maxBuffer=${this.maxFrameBufferSize}`
    );
  }

  /**
   * T017/T035: Filter pose matrix with frame buffer and optimized allocations
   * @param {THREE.Matrix4} matrix - Raw pose matrix from MindAR
   * @returns {THREE.Matrix4} - Smoothed pose matrix
   */
  filter(matrix) {
    if (!matrix) {
      return null;
    }

    // T035: Reuse pre-allocated objects (avoid new allocations in hot path)
    matrix.decompose(this._position, this._quaternion, this._scale);
    this._euler.setFromQuaternion(this._quaternion);

    // T017: Add measurement to frame buffer (FIFO)
    // Note: We still create frame objects here, but only 3 max due to buffer limit
    this.frameBuffer.push({
      position: this._position.clone(),
      euler: this._euler.clone(),
      scale: this._scale.clone(),
      timestamp: performance.now(),
    });

    // T017: Limit buffer size to maxFrameBufferSize (keep latest 3 frames)
    while (this.frameBuffer.length > this.maxFrameBufferSize) {
      this.frameBuffer.shift(); // Remove oldest frame
    }

    // Initialize on first detection
    if (!this.isInitialized) {
      this.positionFilters.x.x = this._position.x;
      this.positionFilters.y.x = this._position.y;
      this.positionFilters.z.x = this._position.z;
      this.rotationFilters.x.x = this._euler.x;
      this.rotationFilters.y.x = this._euler.y;
      this.rotationFilters.z.x = this._euler.z;
      this.isInitialized = true;
      this.lastMatrix = matrix.clone();
      return matrix;
    }

    // T017: Apply Kalman filter only to latest frame in buffer
    // This ensures latency stays within 3 frames @ 60fps = 50ms
    const latestFrame = this.frameBuffer[this.frameBuffer.length - 1];

    // T045: Angle-Dependent Noise Adjustment
    // Calculate viewing angle (dot product of camera Z and marker Z)
    // matrix elements [8, 9, 10] represent the Z axis of the rotation matrix
    const zAxisZ = matrix.elements[10]; // Cosine of angle between camera Z and marker Z
    const angleRad = Math.acos(Math.abs(zAxisZ));
    const angleDeg = angleRad * (180 / Math.PI);

    // Dynamic noise adjustment
    // Head-on (0-15 deg): High noise (trust model) -> Strong smoothing
    // Angled (>15 deg): Low noise (trust measurement) -> Responsive
    let dynamicR = this.measurementNoise;

    // Dynamic noise adjustment
    // Head-on (0-15 deg): High noise (trust model) -> Strong smoothing
    // Angled (>15 deg): Low noise (trust measurement) -> Responsive

    // T045: Angle-Dependent Noise (Applied to ALL platforms)
    // This is critical for fixing head-on jitter (PnP instability) on both iOS and Android
    if (angleDeg < 15) {
      // Head-on: Increase measurement noise to suppress jitter
      // Linear interpolation from 15deg (base) to 0deg (3x base)
      const factor = 1 + (2 * (15 - angleDeg)) / 15;
      dynamicR = this.measurementNoise * factor;
    }

    // Update filter parameters with dynamic noise
    Object.values(this.positionFilters).forEach((f) => (f.r = dynamicR));
    Object.values(this.rotationFilters).forEach((f) => (f.r = dynamicR));

    // T035: Apply Kalman filtering to position (reuse pre-allocated vector)
    const rawZ = latestFrame.position.z;
    const filteredZ = this.positionFilters.z.update(rawZ);

    // Z-axis damping: If change is very small, ignore it to prevent "pulsing"
    // This is critical for iOS stability when marker is small/distant
    const zDiff = Math.abs(filteredZ - (this._lastFilteredZ || filteredZ));
    const zThreshold = 0.002; // 2mm threshold

    let finalZ = filteredZ;
    if (this._lastFilteredZ !== undefined && zDiff < zThreshold) {
      // Interpolate very slowly towards new value instead of jumping
      finalZ = this._lastFilteredZ + (filteredZ - this._lastFilteredZ) * 0.1;
    }
    this._lastFilteredZ = finalZ;

    this._filteredPosition.set(
      this.positionFilters.x.update(latestFrame.position.x),
      this.positionFilters.y.update(latestFrame.position.y),
      finalZ
    );

    // T035: Apply Kalman filtering to rotation (reuse pre-allocated euler)
    this._filteredEuler.set(
      this.rotationFilters.x.update(latestFrame.euler.x),
      this.rotationFilters.y.update(latestFrame.euler.y),
      this.rotationFilters.z.update(latestFrame.euler.z)
    );

    // T035: Convert back to quaternion (reuse pre-allocated quaternion)
    this._filteredQuaternion.setFromEuler(this._filteredEuler);

    // Compose smoothed matrix
    this.smoothedMatrix.compose(
      this._filteredPosition,
      this._filteredQuaternion,
      latestFrame.scale
    );
    this.lastMatrix = this.smoothedMatrix.clone();

    return this.smoothedMatrix;
  }

  /**
   * Reset filter state (call when poster is lost)
   */
  reset() {
    this.positionFilters.x.reset();
    this.positionFilters.y.reset();
    this.positionFilters.z.reset();
    this.rotationFilters.x.reset();
    this.rotationFilters.y.reset();
    this.rotationFilters.z.reset();
    this.frameBuffer = []; // T017: Clear frame buffer
    this.isInitialized = false;
    this.lastMatrix = null;
    this._lastFilteredZ = undefined; // Reset Z-damping state
    console.log('PoseFilter reset');
  }

  /**
   * Get last filtered matrix (for fallback when tracking lost briefly)
   * @returns {THREE.Matrix4|null}
   */
  getLastMatrix() {
    return this.lastMatrix;
  }

  /**
   * Check if filter is initialized
   * @returns {boolean}
   */
  isReady() {
    return this.isInitialized;
  }

  /**
   * Update filter parameters dynamically
   * @param {number} processNoise - Process noise covariance
   * @param {number} measurementNoise - Measurement noise covariance
   */
  updateParameters(processNoise, measurementNoise) {
    this.processNoise = processNoise;
    this.measurementNoise = measurementNoise;

    // Update all filters
    Object.values(this.positionFilters).forEach((filter) => {
      filter.q = processNoise;
      filter.r = measurementNoise;
    });

    Object.values(this.rotationFilters).forEach((filter) => {
      filter.q = processNoise;
      filter.r = measurementNoise;
    });

    console.log(`PoseFilter parameters updated: q=${processNoise}, r=${measurementNoise}`);
  }
}
